import { stripNonNumeric } from "./stripNonNumeric";
import { DEFAULT_WHATSAPP } from "./constants";

const trimOrEmpty = (v) => (typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim());

const fallbackCompany = (name) => trimOrEmpty(name) || "minha empresa";

/**
 * Mensagens formais com contexto (Portal Terra) — área Minha Conta / compras
 */
export const buildWhatsAppMessageBuyerDetailContact = ({ senderCompanyName, buyerName }) => {
  const company = fallbackCompany(senderCompanyName);
  const buyer = trimOrEmpty(buyerName) || "o comprador";
  return `Olá, tudo bem? Aqui é da ${company}, pelo Portal Terra. Estou falando com ${buyer} sobre compras/pedidos. Pode me ajudar?`;
};

export const buildWhatsAppMessageMasterToBuyerInList = ({ senderCompanyName, buyerName, targetUnitName }) => {
  const company = fallbackCompany(senderCompanyName);
  const buyer = trimOrEmpty(buyerName) || "o comprador";
  const unit = trimOrEmpty(targetUnitName);
  const unitSuffix = unit ? ` na unidade ${unit}` : "";
  return `Olá, tudo bem? Aqui é da ${company}, pelo Portal Terra. Gostaria de falar com ${buyer} sobre compras/pedidos${unitSuffix}.`;
};

export const buildWhatsAppMessageToLinkedUnit = ({ senderCompanyName, targetUnitName }) => {
  const sender = fallbackCompany(senderCompanyName);
  const unit = trimOrEmpty(targetUnitName) || "a unidade";
  return `Olá, tudo bem? Aqui é da ${sender}, pelo Portal Terra. Gostaria de falar sobre a unidade ${unit} e compras/pedidos.`;
};

export const buildWhatsAppMessageToUnitMainContact = ({ senderCompanyName, targetUnitName }) => {
  const sender = fallbackCompany(senderCompanyName);
  const unit = trimOrEmpty(targetUnitName) || "a unidade";
  return `Olá, tudo bem? Aqui é da ${sender}, pelo Portal Terra. Gostaria de falar com o contato principal da unidade ${unit} sobre compras/pedidos.`;
};

export const buildWhatsAppMessageToUnitSeller = ({ senderCompanyName, targetUnitName, sellerName }) => {
  const sender = fallbackCompany(senderCompanyName);
  const unit = trimOrEmpty(targetUnitName) || "a unidade";
  const seller = trimOrEmpty(sellerName);
  const who = seller ? ` com ${seller}` : "";
  return `Olá, tudo bem? Aqui é da ${sender}, pelo Portal Terra. Gostaria de falar${who} como vendedor responsável da unidade ${unit} sobre compras/pedidos.`;
};

export const buildWhatsAppMessageToTerraSeller = ({ userName, companyName }) => {
  const user = trimOrEmpty(userName) || "cliente";
  const company = fallbackCompany(companyName);
  return `Olá, tudo bem? Sou ${user} da ${company}, pelo Portal Terra. Preciso de apoio com compras/pedidos.`;
};

export const buildWhatsAppMessageToCollaborator = ({ senderCompanyName, collaboratorName }) => {
  const company = fallbackCompany(senderCompanyName);
  const collab = trimOrEmpty(collaboratorName) || "o colaborador";
  return `Olá, tudo bem? Aqui é da ${company}, pelo Portal Terra. Gostaria de falar com ${collab} sobre atividades de compras/pedidos.`;
};

export const buildWhatsAppMessageOrderInquiry = ({ companyName, orderNumber }) => {
  const company = fallbackCompany(companyName);
  const num = trimOrEmpty(orderNumber) || "meu pedido";
  return `Olá, tudo bem? Sou da ${company}, pelo Portal Terra. Gostaria de informações sobre o pedido ${num}.`;
};

/**
 * Gera um link do WhatsApp com mensagem opcional
 * @param {string|number} phoneNumber - Número de telefone (com ou sem formatação)
 * @param {string} message - Mensagem opcional para enviar
 * @param {object} options - Opções adicionais
 * @param {boolean} options.useApiFormat - Se true, usa formato api.whatsapp.com, senão usa wa.me (padrão: false)
 * @returns {string} URL do WhatsApp
 */
export const generateWhatsAppLink = (phoneNumber, message = "", options = {}) => {
  if (!phoneNumber) {
    return null;
  }

  const cleanPhone = stripNonNumeric(phoneNumber);
  if (!cleanPhone) {
    return null;
  }

  const encodedMessage = message ? `&text=${encodeURIComponent(message)}` : "";
  const phoneWithCountryCode = cleanPhone.startsWith("55") ? cleanPhone : `55${cleanPhone}`;

  if (options.useApiFormat) {
    return `https://api.whatsapp.com/send?phone=${phoneWithCountryCode}${encodedMessage}`;
  }

  return `https://wa.me/${phoneWithCountryCode}${encodedMessage ? `?text=${encodeURIComponent(message)}` : ""}`;
};

/**
 * Abre o WhatsApp em uma nova aba
 * @param {string|number} phoneNumber - Número de telefone
 * @param {string} message - Mensagem opcional
 * @param {object} options - Opções adicionais
 */
export const openWhatsApp = (phoneNumber, message = "", options = {}) => {
  const link = generateWhatsAppLink(phoneNumber, message, options);
  if (link) {
    window.open(link, "_blank", "noopener,noreferrer");
  }
};

/**
 * Gera link do WhatsApp para o vendedor responsável de um pedido
 * @param {object} orderDetail - Detalhes do pedido
 * @param {object} contactTerra - Contato Terra (fallback)
 * @param {string} defaultMessage - Mensagem padrão (pode incluir {orderNumber})
 * @returns {string} URL do WhatsApp
 */
export const getOrderWhatsAppLink = (orderDetail, contactTerra, defaultMessage = "Olá, gostaria de saber mais sobre o pedido {orderNumber}") => {
  const orderNumber = orderDetail?.order_number || orderDetail?.cart_number || "";
  const message = defaultMessage.replace("{orderNumber}", orderNumber);
  
  // Tenta usar o WhatsApp do vendedor responsável se disponível
  const sellerWhatsApp = orderDetail?.customer_data?.responsible_whatsapp || 
                        orderDetail?.customer_data?.seller_whatsapp;
  
  if (sellerWhatsApp) {
    return generateWhatsAppLink(sellerWhatsApp, message);
  }
  
  // Fallback para o WhatsApp do contato Terra
  if (contactTerra?.whatsapp) {
    return generateWhatsAppLink(contactTerra.whatsapp, message);
  }
  
  // Último fallback para WhatsApp geral
  return generateWhatsAppLink(DEFAULT_WHATSAPP, message, { useApiFormat: true });
};

/**
 * Links pré-configurados do WhatsApp (compatibilidade com contact.js)
 * Usa DEFAULT_WHATSAPP e gera links com mensagens pré-definidas
 */
export const whatsapp_links = {
  geral: generateWhatsAppLink(DEFAULT_WHATSAPP, "Olá, tudo bem? Gostaria de fazer um orçamento!", { useApiFormat: true }),
  parceiro: generateWhatsAppLink(DEFAULT_WHATSAPP, "Olá, tudo bem? Gostaria de me tornar um parceiro Terra!", { useApiFormat: true }),
  ferramentas: generateWhatsAppLink(DEFAULT_WHATSAPP, "Olá, tudo bem? Gostaria de conhecer sua linha de ferramentas!", { useApiFormat: true }),
  fixadores: generateWhatsAppLink(DEFAULT_WHATSAPP, "Olá, tudo bem? Gostaria de conhecer sua linha de fixadores!", { useApiFormat: true }),
  quimicos: generateWhatsAppLink(DEFAULT_WHATSAPP, "Olá, tudo bem? Gostaria de conhecer sua linha química!", { useApiFormat: true }),
  estoque: generateWhatsAppLink(DEFAULT_WHATSAPP, "Olá, tudo bem? Gostaria de conhecer seu estoque!", { useApiFormat: true }),
};

